import React, { useEffect, useMemo, useState } from 'react';
import { useAdminForecastSettings, sanitizeForecastSettings } from '../../hooks/useAdminForecastSettings.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const ForecastSettingsSection = ({ onNotify }) => {
  const {
    settings,
    loading,
    saving,
    error,
    loadSettings,
    saveSettings
  } = useAdminForecastSettings();
  const [formValues, setFormValues] = useState(settings);

  useEffect(() => {
    setFormValues(settings);
  }, [settings]);

  const preview = useMemo(
    () => sanitizeForecastSettings(formValues, settings),
    [formValues, settings]
  );

  const isDirty = useMemo(() => (
    preview.horizon !== settings.horizon
    || preview.confidence_level !== settings.confidence_level
    || preview.default_weeks !== settings.default_weeks
  ), [preview, settings]);

  useLucideIcons([
    preview.horizon,
    preview.confidence_level,
    preview.default_weeks,
    loading,
    saving,
    isDirty
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await saveSettings(preview);
      onNotify?.({ type: 'success', message: 'Configuración de pronóstico guardada correctamente.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron guardar los cambios.' });
    }
  };

  const handleRefresh = async () => {
    try {
      const next = await loadSettings();
      setFormValues(next);
      onNotify?.({ type: 'success', message: 'Parámetros recargados desde el servidor.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron recargar los parámetros.' });
    }
  };

  const handleReset = () => {
    setFormValues(settings);
  };

  const confidencePercent = Math.round(preview.confidence_level * 100);
  const horizonDays = (preview.horizon / 24).toFixed(1);
  const summaryCards = useMemo(() => ([
    {
      label: 'Horizonte de forecast',
      value: `${preview.horizon} h`,
      hint: `${horizonDays} días de cobertura`,
      icon: 'clock-7'
    },
    {
      label: 'Confianza del modelo',
      value: `${confidencePercent}%`,
      hint: 'Escala 0 a 1',
      icon: 'shield-check'
    },
    {
      label: 'Semanas por defecto',
      value: `${preview.default_weeks}`,
      hint: 'Selección inicial en panel',
      icon: 'calendar-range'
    }
  ]), [confidencePercent, horizonDays, preview.default_weeks, preview.horizon]);

  return (
    <section id="forecast-settings-section" className="admin-section active">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-eyebrow">Motor de demanda</div>
          <h2 className="section-title">Pronóstico AI</h2>
          <p className="section-subtitle">
            Ajusta el horizonte y la confianza estadística para que el modelo responda a tu operación.
          </p>
        </div>
        <div className="section-actions">
          <button className="btn-ghost" type="button" onClick={handleRefresh} disabled={loading || saving}>
            <span className="btn-icon"><i data-lucide="refresh-cw" /></span>
            {loading ? 'Cargando...' : 'Recargar'}
          </button>
        </div>
      </div>

      <div className="dashboard-card full-width">
        <div className="card-header">
          <h3 className="card-title">
            <i data-lucide="radar" />
            Resumen operativo
          </h3>
        </div>
        <div className="card-content usage-summary-grid">
          {summaryCards.map((card) => (
            <div className="usage-summary-card" key={card.label}>
              <div className="summary-icon"><i data-lucide={card.icon} /></div>
              <div className="summary-label">{card.label}</div>
              <div className="summary-value">{card.value}</div>
              <div className="summary-hint">{card.hint}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-card full-width">
        <div className="card-header">
          <h3 className="card-title">
            <i data-lucide="wand-sparkles" />
            Parámetros del modelo
          </h3>
        </div>
        <div className="card-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Cargando configuración...</div>
            </div>
          ) : (
            <form id="forecast-settings-form" className="form-grid" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="horizon">Horizonte (horas)</label>
                <input
                  id="horizon"
                  name="horizon"
                  type="number"
                  className="form-input"
                  min="1"
                  max="720"
                  step="1"
                  value={formValues.horizon ?? ''}
                  onChange={handleChange}
                  required
                />
                <p className="helper-text">Cuántas horas hacia adelante se pronostican ventas.</p>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="confidence_level">Nivel de confianza</label>
                <input
                  id="confidence_level"
                  name="confidence_level"
                  type="number"
                  className="form-input"
                  min="0.01"
                  max="1"
                  step="0.01"
                  value={formValues.confidence_level ?? ''}
                  onChange={handleChange}
                  required
                />
                <p className="helper-text">Probabilidad objetivo (0 a 1) para las bandas de forecast.</p>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="default_weeks">Semanas seleccionadas por defecto</label>
                <input
                  id="default_weeks"
                  name="default_weeks"
                  type="number"
                  className="form-input"
                  min="1"
                  max="12"
                  step="1"
                  value={formValues.default_weeks ?? ''}
                  onChange={handleChange}
                  required
                />
                <p className="helper-text">Cuántas semanas se muestran preseleccionadas al abrir el modelo.</p>
              </div>
              <div className="form-group">
                <div className="button-group">
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={handleReset}
                    disabled={!isDirty || saving}
                  >
                    <span className="btn-icon"><i data-lucide="rotate-ccw" /></span>
                    Restablecer cambios
                  </button>
                  <button className="btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar configuración'}
                  </button>
                </div>
                <p className="helper-text">
                  Los cambios se aplican de inmediato al generar nuevos pronósticos.
                </p>
              </div>
              {error ? <div className="form-error" role="alert">{error}</div> : null}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ForecastSettingsSection;
